/**
 * Canvases — detail-only page. The canvas inventory now lives in the
 * Studio sidebar (StudioMenu); this page just renders the detail body
 * for whichever canvas is selected via `?canvas=<slug>`. When nothing
 * is selected the page falls back to the StudioOverview library
 * landing.
 *
 * Deep link: `?view=canvases&canvas=<slug>` selects the detail. Used
 * by the post-save toast in ChatCanvas and by the sidebar's ItemRow
 * clicks.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import SecretFindingOverrideDialog from "../components/SecretFindingOverrideDialog.jsx";
import CanvasRowsEditor from "../components/CanvasRowsEditor.jsx";
import CanvasItems from "../components/lenses/CanvasItems.jsx";
import HiddenOriginDeploymentViews from "../components/HiddenOriginDeploymentViews.jsx";
import PortableTenantViewArtifactActions from "../components/PortableTenantViewArtifactActions.jsx";
import StudioOverview from "../components/StudioOverview.jsx";
import SplitPage from "../components/SplitPage.jsx";
import ScrollArea from "../components/ScrollArea.jsx";
import TabStrip from "../components/TabStrip.jsx";
import TenantViewLifecycleNotice from "../components/TenantViewLifecycleNotice.jsx";
import { triggerExportPng } from "../components/ExportableBlock.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { normalizeRows, cellCount, flattenCells } from "../lib/canvasLayout.js";
import { getCurrentTenantIdSync } from "../lib/userInfo.js";
import {
    bundledCanvasRunConcurrency,
    listCanvasTemplates,
    saveCanvasTemplateCloudFirst,
    deleteCanvasTemplate,
    forkCanvasTemplateCloudFirst,
    isBuiltinCanvas,
    isBundledCanvasDefinition,
    isStoredCanvasDefinition,
    sanitizeSlug,
    subscribeCanvasTemplates,
} from "../lib/canvasTemplateStore.js";
import {
    kernelResolverForTenant,
    listKernels,
    subscribeKernels,
} from "../lib/kernelStore.js";
import {
    INVOCATION_SOURCES,
    RUN_SURFACES,
    createBundledRunContext,
    createLiveRunContext,
    createStoredRunContext,
    createUnknownRunContext,
    isLegacyLocalCanvas,
    legacyCommandCanvasCellAdapter,
    resolveCanvasCellKernel,
} from "../lib/kernelResolver.js";
import {
    deleteCloudTenantView,
    forkCloudTenantView,
    hideCloudTenantView,
    loadTenantViewBody,
    publishCloudTenantView,
    unpublishCloudTenantView,
    updateCloudTenantView,
} from "../lib/tenantViewCloudStore.js";
import {
    inspectTenantViewSecretFindings,
    recordTenantViewOpen,
    recordTenantViewRun,
} from "../lib/tenantViewsApi.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

const inputCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";

const SERVER_CANVAS_FIELDS = Object.freeze([
    "visibility",
    "version",
    "createdBy",
    "created_by",
    "objectOrigin",
    "object_origin",
    "forkedFrom",
    "forked_from",
    "canEdit",
    "can_edit",
    "canDelete",
    "can_delete",
    "canFork",
    "can_fork",
    "canPublish",
    "can_publish",
    "canOverrideSecrets",
    "can_override_secrets",
    "canUnpublish",
    "can_unpublish",
]);

function hasOwn(value, key) {
    return value != null && Object.prototype.hasOwnProperty.call(value, key);
}

function readBoolean(value, camel, snake) {
    if (hasOwn(value, camel)) return value[camel] === true;
    if (hasOwn(value, snake)) return value[snake] === true;
    return false;
}

function lifecycleFailure(action, error) {
    const detail = error?.message || String(error || "Unknown error");
    return `${action} failed: ${detail}`;
}

/**
 * Split a canvas's stored kernel dependencies into the ones this actor can
 * promote and the ones they cannot. The server refuses to publish a canvas
 * that references an invisible kernel, so publishing the canvas promotes its
 * drafts first. Bundled cells and kernels absent from the readable catalog are
 * left to the server, which allows the former and reports the latter.
 */
function unresolvedCanvasKernels(canvas, kernels) {
    const byId = new Map(kernels.map((kernel) => [kernel.id, kernel]));
    const seen = new Set();
    const publishable = [];
    const blocked = [];
    for (const row of normalizeRows(canvas)) {
        for (const cell of row.cells) {
            const id = cell?.kernel_id;
            if (typeof id !== "string" || !id || seen.has(id)) continue;
            seen.add(id);
            const kernel = byId.get(id);
            if (!kernel?.tenantViewId || kernel.visibility === "published") continue;
            if (readBoolean(kernel, "canPublish", "can_publish")) publishable.push(kernel);
            else blocked.push(kernel);
        }
    }
    return { publishable, blocked };
}

/**
 * Normalize the server's per-row authorization decisions for the canvas UI.
 *
 * Existing local-only canvases predate these fields, so a row with none of
 * them keeps the old owner behavior. Once any server-shaped affordance is
 * present, every omitted field fails closed. Built-ins remain immutable even
 * if malformed metadata claims otherwise; their historical fork affordance is
 * retained only when the server has not explicitly denied it.
 */
export function getCanvasAffordances(template) {
    const builtin = template?.builtin === true || isBuiltinCanvas(template?.slug);
    const isServerShaped = SERVER_CANVAS_FIELDS.some((key) => hasOwn(template, key));
    const hasForkDecision = hasOwn(template, "canFork") || hasOwn(template, "can_fork");

    if (builtin) {
        return {
            isBuiltin: true,
            canEdit: false,
            canDelete: false,
            canFork: hasForkDecision ? readBoolean(template, "canFork", "can_fork") : true,
            canPublish: false,
        };
    }

    if (!isServerShaped) {
        return {
            isBuiltin: false,
            canEdit: true,
            canDelete: true,
            canFork: false,
            canPublish: false,
        };
    }

    return {
        isBuiltin: false,
        canEdit: readBoolean(template, "canEdit", "can_edit"),
        canDelete: readBoolean(template, "canDelete", "can_delete"),
        canFork: readBoolean(template, "canFork", "can_fork"),
        canPublish: readBoolean(template, "canPublish", "can_publish"),
    };
}

export default function CanvasesPage({ selectedItem, onSelectItem, onRenameItem, onCloseItem, focusHeader }) {
    const [templates, setTemplates] = useState(() => listCanvasTemplates(null));
    useEffect(() => subscribeCanvasTemplates(() => setTemplates(listCanvasTemplates(null))), []);
    const [kernels, setKernels] = useState(() => listKernels(null));
    useEffect(() => subscribeKernels(() => setKernels(listKernels(null))), []);

    const selectedSlug = selectedItem || null;
    const setSelectedSlug = useCallback((slug) => {
        onSelectItem?.(slug ? "canvases" : null, slug || null);
    }, [onSelectItem]);
    const navigateTo = useCallback((view, itemId) => {
        onSelectItem?.(view, itemId);
    }, [onSelectItem]);

    // A rename lands in the catalog before the selection param catches up.
    // Resolving the new slug across that gap keeps the detail pane mounted
    // instead of dropping the user back on the library landing for a frame.
    const [renamedTo, setRenamedTo] = useState(null);
    const selectedTemplate = useMemo(
        () => templates.find((t) => t.slug === selectedSlug)
            || (renamedTo ? templates.find((t) => t.slug === renamedTo) : null)
            || null,
        [templates, selectedSlug, renamedTo],
    );
    useEffect(() => {
        if (renamedTo && selectedSlug === renamedTo) setRenamedTo(null);
    }, [renamedTo, selectedSlug]);
    // A rename moves the open tab onto the new slug. Selecting it instead would
    // add a second tab and leave the first one on a slug that no longer exists.
    const onRenamed = useCallback((slug) => {
        setRenamedTo(slug);
        onRenameItem?.("canvases", selectedSlug, slug);
    }, [onRenameItem, selectedSlug]);
    const [lifecycleNotice, setLifecycleNotice] = useState(null);
    const [pendingSecretPublish, setPendingSecretPublish] = useState(null);
    const lifecycleBusyRef = useRef(null);
    const [lifecycleBusy, setLifecycleBusy] = useState(null);
    const beginLifecycle = useCallback((action) => {
        if (lifecycleBusyRef.current) return false;
        lifecycleBusyRef.current = action;
        setLifecycleBusy(action);
        return true;
    }, []);
    const finishLifecycle = useCallback(() => {
        lifecycleBusyRef.current = null;
        setLifecycleBusy(null);
    }, []);
    const [bodyLoadError, setBodyLoadError] = useState(null);
    useEffect(() => {
        setBodyLoadError(null);
        if (!selectedTemplate?.tenantViewId || selectedTemplate.bodyLoaded === true) {
            return undefined;
        }
        const controller = new AbortController();
        loadTenantViewBody(selectedTemplate.tenantViewId, {
            signal: controller.signal,
            withDependencies: true,
        }).catch((error) => {
            if (error?.name !== "AbortError") setBodyLoadError(error);
        });
        return () => controller.abort();
    }, [selectedTemplate?.bodyLoaded, selectedTemplate?.tenantViewId]);

    const [pendingDelete, setPendingDelete] = useState(null);
    const confirmDelete = useCallback(async () => {
        const slug = pendingDelete;
        if (!slug) return;
        const target = templates.find((template) => template.slug === slug);
        if (!target || !getCanvasAffordances(target).canDelete) {
            setPendingDelete(null);
            return;
        }
        if (!beginLifecycle("delete")) return;
        try {
            if (target.tenantViewId) await deleteCloudTenantView(target);
            else deleteCanvasTemplate(null, slug);
            // Closing the tab also moves the selection off the deleted canvas.
            if (onCloseItem) onCloseItem("canvases", slug);
            else if (selectedSlug === slug) setSelectedSlug(null);
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Delete canvas", error));
        } finally {
            // Close the stale confirmation snapshot after an API/CAS failure;
            // the canvas remains selected and can be retried explicitly.
            setPendingDelete(null);
            finishLifecycle();
        }
    }, [
        beginLifecycle,
        finishLifecycle,
        onCloseItem,
        pendingDelete,
        selectedSlug,
        setSelectedSlug,
        templates,
    ]);

    // Fork a (read-only built-in, or any) canvas into a mutable user copy and
    // jump to the copy.
    const onFork = useCallback(async (slug) => {
        const target = templates.find((template) => template.slug === slug);
        if (!target || !getCanvasAffordances(target).canFork) return;
        if (!beginLifecycle("fork")) return;
        try {
            const copy = target.tenantViewId || target.builtin
                ? await forkCloudTenantView(target)
                : await forkCanvasTemplateCloudFirst(null, slug);
            if (copy) navigateTo("canvases", copy.slug);
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Fork canvas", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, navigateTo, templates]);

    const onTogglePublish = useCallback(async (template) => {
        if (!template?.tenantViewId) return;
        const target = templates.find(
            (candidate) => candidate.tenantViewId === template.tenantViewId,
        );
        if (!target) return;
        const action = target.visibility === "published" ? "unpublish" : "publish";
        if (action === "unpublish") {
            if (!readBoolean(target, "canUnpublish", "can_unpublish")) return;
        } else if (!getCanvasAffordances(target).canPublish) {
            return;
        }
        if (!beginLifecycle(action)) return;
        try {
            if (action === "unpublish") {
                const updated = await unpublishCloudTenantView(target);
                const count = updated.visibleDependents?.length || 0;
                setLifecycleNotice(count
                    ? `Canvas unpublished. ${count} visible dependent view${count === 1 ? "" : "s"} was affected.`
                    : "Canvas unpublished.");
            } else {
                const { publishable, blocked } = unresolvedCanvasKernels(target, kernels);
                if (blocked.length > 0) {
                    throw new Error(
                        `this canvas depends on ${blocked.map((kernel) => `/${kernel.command}`).join(", ")}, which you cannot publish. Ask their author to publish them first.`,
                    );
                }
                for (const kernel of publishable) {
                    try {
                        await publishCloudTenantView(kernel);
                    } catch (error) {
                        throw new Error(
                            `kernel /${kernel.command} could not be published (${error?.serverMessage || error?.message || error}). Publish it from Kernels, then publish this canvas.`,
                        );
                    }
                }
                const inspection = await inspectTenantViewSecretFindings(
                    target.tenantViewId,
                );
                if (
                    target.version != null
                    && inspection.version !== String(target.version)
                ) {
                    throw new Error("The canvas changed. Refresh and review it before publishing.");
                }
                if (inspection.totalFindingCount > 0) {
                    if (inspection.findingsTruncated) {
                        throw new Error(
                            "Publishing is blocked because the secret-finding list is truncated.",
                        );
                    }
                    if (!target.canOverrideSecrets) {
                        throw new Error(
                            "Publishing is blocked by secret-like content; tenant management permission is required to review false positives.",
                        );
                    }
                    setPendingSecretPublish({ target, inspection });
                } else {
                    await publishCloudTenantView(target);
                    setLifecycleNotice(publishable.length
                        ? `Canvas published, along with ${publishable.length} kernel${publishable.length === 1 ? "" : "s"} it depends on.`
                        : "Canvas published.");
                }
            }
        } catch (error) {
            setLifecycleNotice(lifecycleFailure(
                target.visibility === "published" ? "Unpublish canvas" : "Publish canvas",
                error,
            ));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, kernels, templates]);
    const confirmSecretPublish = useCallback(async (secretOverrides) => {
        const pending = pendingSecretPublish;
        if (!pending || !beginLifecycle("publish")) return;
        try {
            await publishCloudTenantView(pending.target, { secretOverrides });
            setPendingSecretPublish(null);
            setLifecycleNotice("Canvas published.");
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Publish canvas", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, pendingSecretPublish]);
    const onHide = useCallback(async (template) => {
        const target = templates.find(
            (candidate) => candidate.tenantViewId === template?.tenantViewId,
        );
        if (!target?.canHide) return;
        if (!beginLifecycle("hide")) return;
        try {
            const updated = await hideCloudTenantView(target);
            const count = updated.visibleDependents?.length || 0;
            setLifecycleNotice(count
                ? `Official canvas hidden. ${count} visible dependent view${count === 1 ? "" : "s"} was affected.`
                : "Official canvas hidden.");
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Hide official canvas", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, templates]);

    if (!selectedTemplate) {
        return (
            <>
                <TenantViewLifecycleNotice
                    message={lifecycleNotice}
                    onDismiss={() => setLifecycleNotice(null)}
                />
                <PortableTenantViewArtifactActions
                    className="px-[var(--main-inset)] pb-[8px]"
                />
                <StudioOverview
                    kernels={kernels}
                    canvases={templates}
                    onOpenKernel={(cmd) => navigateTo("kernels", cmd)}
                    onOpenCanvas={(slug) => navigateTo("canvases", slug)}
                />
                <HiddenOriginDeploymentViews kind="canvas" />
                {pendingSecretPublish ? (
                    <SecretFindingOverrideDialog
                        title="Review canvas secret findings"
                        findings={pendingSecretPublish.inspection.findings}
                        contentDigest={pendingSecretPublish.inspection.contentDigest}
                        detectorVersion={pendingSecretPublish.inspection.detectorVersion}
                        busy={lifecycleBusy === "publish"}
                        onCancel={() => setPendingSecretPublish(null)}
                        onConfirm={confirmSecretPublish}
                    />
                ) : null}
            </>
        );
    }

    if (selectedTemplate.tenantViewId && selectedTemplate.bodyLoaded !== true) {
        return (
            <div
                className="flex-1 grid place-items-center text-[12px] text-[color:var(--muted)]"
                aria-busy={bodyLoadError ? undefined : "true"}
                role={bodyLoadError ? "alert" : "status"}
            >
                {bodyLoadError
                    ? `Could not load canvas: ${bodyLoadError.message || bodyLoadError}`
                    : "Loading canvas…"}
            </div>
        );
    }

    const affordances = getCanvasAffordances(selectedTemplate);

    return (
        <>
            <TenantViewLifecycleNotice
                message={lifecycleNotice}
                onDismiss={() => setLifecycleNotice(null)}
            />
            {pendingSecretPublish ? (
                <SecretFindingOverrideDialog
                    title="Review canvas secret findings"
                    findings={pendingSecretPublish.inspection.findings}
                    contentDigest={pendingSecretPublish.inspection.contentDigest}
                    detectorVersion={pendingSecretPublish.inspection.detectorVersion}
                    busy={lifecycleBusy === "publish"}
                    onCancel={() => setPendingSecretPublish(null)}
                    onConfirm={confirmSecretPublish}
                />
            ) : null}
            <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
                <CanvasDetailBody
                    key={selectedTemplate.tenantViewId || selectedTemplate.id || selectedTemplate.slug}
                    template={selectedTemplate}
                    kernels={kernels}
                    focusHeader={focusHeader}
                    affordances={affordances}
                    lifecycleBusy={lifecycleBusy}
                    onRenamed={onRenamed}
                    onDelete={affordances.canDelete ? () => setPendingDelete(selectedTemplate.slug) : null}
                    onFork={affordances.canFork ? () => onFork(selectedTemplate.slug) : null}
                    onTogglePublish={
                        selectedTemplate.tenantViewId
                        && (selectedTemplate.visibility === "published"
                            ? readBoolean(selectedTemplate, "canUnpublish", "can_unpublish")
                            : affordances.canPublish)
                            ? () => onTogglePublish(selectedTemplate)
                            : null
                    }
                    onHide={selectedTemplate.canHide
                        ? () => onHide(selectedTemplate)
                        : null}
                />
            </div>
            <ConfirmDialog
                open={pendingDelete != null}
                analyticsId={DIALOG.CANVAS_DELETE}
                eyebrow="Canvas"
                title="Delete saved canvas?"
                body={pendingDelete ? (
                    <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                        This will permanently delete <span className="font-mono text-[color:var(--ink)]">/{pendingDelete}</span> from
                        your saved canvases. Its kernels will remain available.
                    </p>
                ) : null}
                confirmLabel="Delete"
                confirmDisabled={lifecycleBusy != null}
                destructive
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </>
    );
}

// ── Detail body ──────────────────────────────────────────────────

export function CanvasDetailBody({
    template,
    kernels,
    focusHeader,
    affordances = getCanvasAffordances(template),
    lifecycleBusy = null,
    onRenamed,
    onDelete,
    onFork,
    onTogglePublish,
    onHide,
}) {
    const tenantId = getCurrentTenantIdSync();
    const { isBuiltin, canEdit, canDelete, canFork } = affordances;
    const kernelResolver = useMemo(
        () => kernelResolverForTenant(null),
        [kernels],
    );
    const isLegacyCanvas = isLegacyLocalCanvas(template);
    const legacyCellAdapter = isLegacyCanvas
        ? legacyCommandCanvasCellAdapter
        : null;
    const invocationSource = isLegacyCanvas
        ? INVOCATION_SOURCES.UNKNOWN
        : isBundledCanvasDefinition(template)
            ? INVOCATION_SOURCES.BUNDLED
            : isStoredCanvasDefinition(template)
                ? INVOCATION_SOURCES.STORED
                : INVOCATION_SOURCES.UNKNOWN;
    const canvasRunContext = useMemo(
        () => {
            const metadata = { surface: RUN_SURFACES.CANVAS };
            if (invocationSource === INVOCATION_SOURCES.BUNDLED) {
                return createBundledRunContext(metadata);
            }
            if (invocationSource === INVOCATION_SOURCES.STORED) {
                return createStoredRunContext(metadata);
            }
            return createUnknownRunContext(metadata);
        },
        [invocationSource],
    );
    const editorRunContext = useMemo(
        () => createLiveRunContext({
            surface: RUN_SURFACES.EDITOR,
        }),
        [],
    );

    const [version, setVersion] = useState(0);
    const [lastRunAt, setLastRunAt] = useState(() => Date.now());
    const refreshAll = useCallback(() => {
        setVersion((v) => v + 1);
        setLastRunAt(Date.now());
    }, []);
    const reportedOpenId = useRef(null);
    useEffect(() => {
        const id = isStoredCanvasDefinition(template)
            ? template.tenantViewId || null
            : null;
        if (reportedOpenId.current === id) return;
        reportedOpenId.current = id;
        if (!id) return;
        void recordTenantViewOpen(id).catch((error) => {
            console.warn("[canvases] open telemetry failed", error);
        });
    }, [template]);
    const reportRunOutcome = useCallback((event) => {
        if (!isStoredCanvasDefinition(template) || !template.tenantViewId) return;
        void recordTenantViewRun(template.tenantViewId, {
            outcome: event.outcome,
            reasonCategory: event.reasonCategory,
            analyticsQueryCount: event.analyticsQueryCount ?? 0,
        }).catch((error) => {
            console.warn("[canvases] run telemetry failed", error);
        });
    }, [template]);

    // PNG export of the whole canvas (header + all kernel widgets).
    // Reuses the chat-side triggerExportPng so the [0] Venom rail,
    // padding, and pixel ratio match every other dashboard export.
    const exportRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const handleExport = useCallback(async () => {
        if (exporting || !exportRef.current) return;
        setExporting(true);
        try {
            await triggerExportPng(exportRef.current, `canvas-${template.slug}-${Date.now()}.png`);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[canvases] export failed:", err);
        } finally {
            setExporting(false);
        }
    }, [exporting, template.slug]);

    // ── Edit mode ───────────────────────────────────────────────────
    const [editing, setEditing] = useState(false);
    const [draftSlug, setDraftSlug] = useState(template.slug);
    const [draftTitle, setDraftTitle] = useState(template.title);
    const [draftDescription, setDraftDescription] = useState(template.description || "");
    const [draftRows, setDraftRows] = useState(() => normalizeRows(template));
    const [editError, setEditError] = useState(null);
    const [editWarning, setEditWarning] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const saveInFlight = useRef(false);
    const [saving, setSaving] = useState(false);

    // Reset transient edit state when a different canvas is selected. Keyed on
    // identity, not slug: a rename keeps the user on the canvas they were
    // editing, and must not discard a durability warning the save just raised.
    const templateIdentity = template.tenantViewId || template.id || template.slug;
    useEffect(() => {
        setEditing(false);
        setEditWarning(null);
        setPickerOpen(false);
    }, [templateIdentity, canEdit]);

    const enterEdit = () => {
        if (!canEdit) return;
        setDraftSlug(template.slug);
        setDraftTitle(template.title);
        setDraftDescription(template.description || "");
        setDraftRows(normalizeRows(template));
        setEditError(null);
        setEditWarning(null);
        setPickerOpen(false);
        setEditing(true);
    };
    const cancelEdit = () => {
        if (saveInFlight.current) return;
        setEditing(false);
        setEditError(null);
        setEditWarning(null);
        setPickerOpen(false);
    };
    const submitEdit = async () => {
        if (!canEdit || saveInFlight.current) return;
        if (!flattenCells({ rows: draftRows }).length) {
            setEditError("a canvas must have at least one kernel");
            return;
        }
        const nextSlug = sanitizeSlug(draftSlug);
        if (!nextSlug) {
            setEditError("a slug needs at least one letter or number");
            return;
        }
        if (nextSlug !== template.slug) {
            if (isBuiltinCanvas(nextSlug)) {
                setEditError(`"${nextSlug}" belongs to a built-in canvas. Choose another slug.`);
                return;
            }
            if (listCanvasTemplates(null).some((other) => other.slug === nextSlug)) {
                setEditError(`"${nextSlug}" is already used by another canvas.`);
                return;
            }
        }
        saveInFlight.current = true;
        setSaving(true);
        try {
            const next = {
                ...template,
                slug: nextSlug,
                title: draftTitle.trim() || nextSlug,
                description: draftDescription.trim() || null,
                rows: draftRows,
            };
            const saved = template.tenantViewId
                ? await updateCloudTenantView(template, next)
                : await saveCanvasTemplateCloudFirst(null, next);
            // The local row moves even when the cloud write is deferred, so
            // follow the slug before reporting anything else about the save.
            if (saved?.slug && saved.slug !== template.slug) onRenamed?.(saved.slug);
            if (saved?.cloudSaveStatus) {
                throw new Error(saved.cloudSaveMessage || "This canvas is still stored locally.");
            }
            if (saved?.localDurabilityWarning) {
                setEditError(null);
                setEditWarning(saved.localDurabilityWarning);
                return;
            }
            setEditing(false);
            setEditError(null);
            setEditWarning(null);
        } catch (err) {
            setEditError(err?.message || String(err));
        } finally {
            saveInFlight.current = false;
            setSaving(false);
        }
    };
    const addItem = (kernel) => {
        if (!kernel?.command) return;
        setDraftRows((prev) => [...prev, {
            cells: [{
                kernel_id: kernel.id,
                command: kernel.command,
            }],
        }]);
        setPickerOpen(false);
    };
    const usedCommands = useMemo(
        () => new Set(flattenCells({ rows: draftRows }).map((cell) => cell.command)),
        [draftRows],
    );
    const availableKernels = useMemo(
        () => kernels.filter((k) => !usedCommands.has(k.command)),
        [kernels, usedCommands],
    );

    const isEditing = editing && canEdit;
    const itemCount = isEditing
        ? flattenCells({ rows: draftRows }).length
        : cellCount(template);
    const rowsForView = useMemo(() => normalizeRows(template), [template]);

    const detailHeader = (
        <TabStrip
            tabs={[{
                id: "canvas-detail",
                label: "Details",
                active: true,
                dot: "bg-[color:var(--green)]",
            }]}
            className="pt-0"
        />
    );

    const renderColumn = (
        <ScrollArea
            data-content-scroll
            className="min-h-0 h-full"
            contentClassName="pt-[14px] pb-[18px]"
        >
            <div ref={exportRef} className="flex flex-col gap-[12px] min-w-0 [overflow-wrap:anywhere]">
                {isEditing ? (
                    <>
                        <CanvasRowsEditor
                            rows={draftRows}
                            kernelResolver={kernelResolver}
                            legacyCellAdapter={legacyCellAdapter}
                            runContext={editorRunContext}
                            version={version}
                            onChange={setDraftRows}
                        />
                        {!flattenCells({ rows: draftRows }).length ? (
                            <div className="rounded-[2px] border border-dashed border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[12px] text-[11px] text-[color:var(--muted)]">
                                This canvas is empty. Select <span className="font-medium">Cancel</span> to restore its items, or add a kernel before saving.
                            </div>
                        ) : null}
                        <AddKernelPicker
                            open={pickerOpen}
                            onToggle={() => setPickerOpen((v) => !v)}
                            available={availableKernels}
                            onPick={addItem}
                        />
                    </>
                ) : (
                    <CanvasItems
                        rows={rowsForView}
                        kernelResolver={kernelResolver}
                        legacyCellAdapter={legacyCellAdapter}
                        runContext={canvasRunContext}
                        kernelParams={null}
                        runtime={template.runtime}
                        canvasKey={template.tenantViewId || template.id || template.slug}
                        tenantId={tenantId}
                        runConcurrency={bundledCanvasRunConcurrency(template)}
                        version={version}
                        onRunOutcome={reportRunOutcome}
                        emptyNote="This canvas has no widgets yet. Click Edit to add kernels."
                    />
                )}
            </div>
        </ScrollArea>
    );

    const metadataColumn = (
        <ScrollArea
            data-content-scroll
            className="min-h-0 h-full"
            contentClassName="pt-[14px] pb-[18px]"
        >
            <div className="grid gap-[14px] min-w-0 [overflow-wrap:anywhere]">
                    {!canEdit ? (
                        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]">
                            <span className="inline-flex w-fit items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted)]">
                                {isBuiltin ? "Built-in · read-only" : "Shared · read-only"}
                            </span>
                            <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                {isBuiltin
                                    ? "Shipped with the dashboard. Fork it to get an editable copy under a new slug."
                                    : canFork
                                        ? "Published by a teammate. Fork it to get an editable copy under a new slug."
                                        : "Your current access permits viewing this canvas only."}
                            </span>
                        </div>
                    ) : null}
                    <div className="grid gap-[10px] grid-cols-1">
                        {isEditing ? null : (
                            <DetailField label="Slug" value={`/${template.slug}`} mono />
                        )}
                        <DetailField label="Kernels" value={String(itemCount)} />
                        <DetailField label="Updated" value={template.updatedAt ? new Date(template.updatedAt).toLocaleString() : ""} />
                        <DetailField label="Created" value={template.createdAt ? new Date(template.createdAt).toLocaleString() : ""} />
                        {isEditing ? null : (
                            <>
                                <DetailField label="Title" value={template.title} />
                                {template.description ? (
                                    <DetailField label="Description" value={template.description} />
                                ) : null}
                            </>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="grid gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[12px] py-[10px]">
                            <label className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Slug</span>
                                <input
                                    type="text"
                                    value={draftSlug}
                                    onChange={(e) => setDraftSlug(e.target.value)}
                                    className={`${inputCls} font-mono`}
                                />
                                <span className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
                                    Names this canvas in its shareable link and its exported PNG. Links you have already shared under the old slug will stop working.
                                </span>
                            </label>
                            <label className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Title</span>
                                <input
                                    type="text"
                                    value={draftTitle}
                                    onChange={(e) => setDraftTitle(e.target.value)}
                                    className={inputCls}
                                />
                            </label>
                            <label className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Description</span>
                                <input
                                    type="text"
                                    value={draftDescription}
                                    onChange={(e) => setDraftDescription(e.target.value)}
                                    placeholder="optional"
                                    className={inputCls}
                                />
                            </label>
                            {editError ? (
                                <span className="text-[11px] text-red-400">{editError}</span>
                            ) : null}
                            {editWarning ? (
                                <span role="alert" className="text-[11px] text-amber-400">
                                    {editWarning}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="flex flex-wrap items-baseline gap-[8px]">
                        {isEditing ? (
                            <>
                                <button
                                    type="button"
                                    onClick={submitEdit}
                                    disabled={saving}
                                    className="cursor-pointer rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[3px] text-[10px] uppercase tracking-[0.04em] font-medium text-[color:var(--ink)] hover:border-[color:var(--green)] disabled:cursor-wait disabled:opacity-60"
                                >
                                    {saving
                                        ? "Saving…"
                                        : editWarning
                                            ? "Retry local recovery"
                                            : "Save"}
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelEdit}
                                    disabled={saving}
                                    className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={refreshAll}
                                    className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                                    title="Re-run every kernel against fresh data"
                                >
                                    Refresh all
                                </button>
                                {canFork ? (
                                    <button
                                        type="button"
                                        onClick={onFork}
                                        disabled={lifecycleBusy != null}
                                        aria-busy={lifecycleBusy === "fork" ? "true" : undefined}
                                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                                        title="Create an editable copy under a new slug"
                                    >
                                        {lifecycleBusy === "fork" ? "Forking…" : "Fork to customize"}
                                    </button>
                                ) : null}
                                {canEdit ? (
                                    <button
                                        type="button"
                                        onClick={enterEdit}
                                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                                    >
                                        Edit
                                    </button>
                                ) : null}
                                {canDelete ? (
                                    <button
                                        type="button"
                                        onClick={onDelete}
                                        disabled={lifecycleBusy != null}
                                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        Delete
                                    </button>
                                ) : null}
                                {onTogglePublish ? (
                                    <button
                                        type="button"
                                        onClick={onTogglePublish}
                                        disabled={lifecycleBusy != null}
                                        aria-busy={
                                            lifecycleBusy === "publish"
                                            || lifecycleBusy === "unpublish"
                                                ? "true"
                                                : undefined
                                        }
                                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {lifecycleBusy === "publish"
                                            ? "Publishing…"
                                            : lifecycleBusy === "unpublish"
                                                ? "Unpublishing…"
                                                : template.visibility === "published"
                                                    ? "Unpublish"
                                                    : "Publish"}
                                    </button>
                                ) : null}
                                {onHide ? (
                                    <button
                                        type="button"
                                        onClick={onHide}
                                        disabled={lifecycleBusy != null}
                                        aria-busy={lifecycleBusy === "hide" ? "true" : undefined}
                                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {lifecycleBusy === "hide" ? "Hiding…" : "Hide official canvas"}
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-progress disabled:opacity-60"
                                    title="Export this canvas as a PNG with the standard sidebar"
                                >
                                    {exporting ? "Exporting…" : "↓ png"}
                                </button>
                            </>
                        )}
                    </div>
                    <PortableTenantViewArtifactActions
                        canvas={template}
                        className="justify-start"
                    />
                {!isEditing ? (
                    <span className="text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]" title={`Last refreshed ${new Date(lastRunAt).toLocaleString()}`}>
                        Rendered {formatAgo(lastRunAt)}
                    </span>
                ) : null}
            </div>
        </ScrollArea>
    );

    return (
        <SplitPage
            defaultDetailRatio={0.36}
            focusHeader={focusHeader}
            rightHeader={detailHeader}
            leftContent={() => renderColumn}
            rightContent={() => metadataColumn}
        />
    );
}

function AddKernelPicker({ open, onToggle, available, onPick }) {
    return (
        <div className="rounded-[2px] border border-dashed border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[10px]">
            <div className="flex items-baseline justify-between">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>
                    Add a kernel to this canvas
                </span>
                <button
                    type="button"
                    onClick={onToggle}
                    className="cursor-pointer bg-transparent text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                >
                    {open ? "− Close" : "+ Add kernel"}
                </button>
            </div>
            {open ? (
                <div className="mt-[8px] flex flex-col gap-[4px]">
                    {available.length === 0 ? (
                        <span className="text-[11px] text-[color:var(--muted)]">
                            Every saved kernel is already in this canvas.
                        </span>
                    ) : (
                        available.map((k) => (
                            <button
                                key={k.id || k.command}
                                type="button"
                                onClick={() => onPick(k)}
                                className="flex items-baseline gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[6px] text-left cursor-pointer hover:border-[color:var(--line-strong)]"
                            >
                                <span className="shrink-0 font-mono text-[11px] text-[color:var(--ink)]">/{k.command}</span>
                                <span className="min-w-0 flex-1 truncate text-[11px] text-[color:var(--muted)]">
                                    {k.title || k.command}
                                </span>
                                <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">add →</span>
                            </button>
                        ))
                    )}
                </div>
            ) : null}
        </div>
    );
}

function DetailField({ label, value, mono = false }) {
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>{label}</span>
            <span
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] break-words ${mono ? "font-mono" : ""}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}

function formatAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 5000) return "just now";
    if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
}
